<template>
  <div v-if="showBusinessInformation">
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" for="businessName">{{businessNameLabel}} <span>*</span></label>
      <input
        id="businessName"
        type="text"
        class="tla-form-group__input"
        :class="getValidationClass('businessName', 'input')"
        name="businessName"
        v-model="businessName"
        data-vv-name="businessName"
        :data-vv-scope="validationScope"
        v-validate.initial="'required'"
        :placeholder="businessNamePlaceholder">
      <span class="tla-form-group__message tla-form-group__message--spaced" :class="getValidationClass('businessName', 'message')">
        Please enter the business name.
      </span>
    </div>
    <div class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required">{{businessSizeLabel}} <span>*</span></label>
      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in businessSizeOptions"
          :key="item.touchpointValue"
          :collectionName="'businessSize'"
          :inputId="item.touchpointValue"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="false"
          :validationScope="validationScope"
          @businessSize="businessSize = $event">
          <touchpointText slot="touchpoint" :content="item.touchpointName"></touchpointText>
        </touchpoint>
      </div>
      <span class="tla-form-group__message" :class="getValidationClass('businessSize', 'message')">
        Please select an option.
      </span>
    </div>

    <div class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required">{{businessTypeLabel}} <span>*</span></label>

      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in businessTypeOptions"
          :key="item.touchpointValue"
          :collectionName="'businessType'"
          :inputId="item.touchpointValue"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="false"
          :validationScope="validationScope"
          @businessType="businessType = $event">
          <touchpointText slot="touchpoint" :content="item.touchpointName"></touchpointText>
        </touchpoint>
      </div>
      <span class="tla-form-group__message" :class="getValidationClass('businessType', 'message')">
        Please select an option.
      </span>
    </div>

    <div class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required">{{businessTimeEstablishedLabel}} <span>*</span></label>

      <div class="tla-form-group__touch-points">
        <touchpoint v-for="(item) in businessTimeEstablishedOptions"
          :key="item.touchpointValue"
          :collectionName="'businessTimeEstablished'"
          :inputId="item.touchpointValue"
          :inputValue="item.touchpointValue"
          :isSelected="item.preSelect"
          :isFullWidth="false"
          :validationScope="validationScope"
          @businessTimeEstablished="businessTimeEstablished = $event">
          <touchpointText slot="touchpoint" :content="item.touchpointName"></touchpointText>
        </touchpoint>
      </div>
      <span class="tla-form-group__message" :class="getValidationClass('businessTimeEstablished', 'message')">
        Please select an option.
      </span>
    </div>

    <div class="tla-form-group tla-form-group--margin-large">
      <label for="existingFleetSize" class="tla-form-group__label">{{existingFleetSizeLabel}}</label>
      <NumberInput
        id="existingFleetSize"
        name="existingFleetSize"
        v-model="existingFleetSize"
        :min="0"
        :max="100"
        :step="1"
        :prefix="''"
        :precision="0"
        :slider="true"
        :fieldName="'existingFleetSize'"
        @existingFleetSize="existingFleetSize = $event">
      </NumberInput>
      <vue-slider v-if="!hideSliders" v-model="existingFleetSize" :min="0" :max="100" height="10" :dot-size="26" :tooltip="false" :interval="5" :class="{ 'vue-slider-component--no-value': existingFleetSize === 0 }"></vue-slider>
    </div>

    <div class="tla-form-group tla-form-group--margin-large">
      <label class="tla-form-group__label tla-form-group__label--required">{{requiredFleetSizeLabel}} <span>*</span></label>

      <NumberInput
        id="requiredFleetSize"
        name="requiredFleetSize"
        v-model="requiredFleetSize"
        :min="0"
        :max="10"
        :step="1"
        :prefix="''"
        :precision="0"
        :slider="true"
        :fieldName="'requiredFleetSize'"
        data-vv-name="requiredFleetSize"
        :data-vv-scope="validationScope"
        :moneyValidationClass="getValidationClass('requiredFleetSize', 'input', requiredFleetSizeChanged)"
        v-validate.initial="`min_value:1`"
        @requiredFleetSize="requiredFleetSize = $event">
      </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass('requiredFleetSize', 'message', requiredFleetSizeChanged)">{{ errors.first(`${validationScope}.requiredFleetSize`) }}</span>
      <vue-slider v-if="!hideSliders" v-model="requiredFleetSize" :min="0" :max="10" height="10" :dot-size="26" :tooltip="false" :interval="1" :class="{ 'vue-slider-component--no-value': requiredFleetSize === 0 }"></vue-slider>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch, Inject
} from 'vue-property-decorator';
import vueSlider from 'vue-slider-component';
import VeeValidate, { Validator } from 'vee-validate';
import { validationService } from '@/services/ValidationService';
import CustomerType from '@/enums/CustomerType';
import mobileCheckService from '@/services/MobileCheckService';
import Touchpoint from '../../models/Touchpoint';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';
import IBusiness from '../../models/interfaces/IBusiness';
import BusinessSizeType from '../../enums/business/BusinessSizeType';
import BusinessTimeEstablishedType from '../../enums/business/BusinessTimeEstablishedType';
import BusinessType from '../../enums/business/BusinessType';
import NumberInput from '../widgets/common/NumberInput.vue';
import GenericTouchPointTextComponent from '../ui/GenericTouchPointTextComponent.vue';
import BusinessValidationMessages from '../../validation/business';

Vue.use(VeeValidate, BusinessValidationMessages);

@Component({
  components: {
  touchpoint: GenericTouchPointComponent,
  NumberInput,
  vueSlider,
  touchpointText: GenericTouchPointTextComponent
  }
  })
export default class BusinessInformationComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Business Size (employees)' })
  public businessSizeLabel!: string;

  @Prop({ default: 'Business Type' })
  public businessTypeLabel!: string;

  @Prop({ default: 'Business Name' })
  public businessNameLabel!: string;

  @Prop({ default: 'Enter name here...' })
  public businessNamePlaceholder!: string;

  @Prop({ default: 'Time Established' })
  public businessTimeEstablishedLabel!: string;

  @Prop({ default: 'Current Fleet Size (Optional)' })
  public existingFleetSizeLabel!: string;

  @Prop({ default: 'Number Of Vehicles Required' })
  public requiredFleetSizeLabel!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public showBusinessInfo!: boolean;

  private existingFleetSizeChanged: boolean = false;

  private requiredFleetSizeChanged: boolean = false;
  
  private hideSliders: boolean = false;

  public businessTimeEstablishedOptions: Touchpoint[] = [
    new Touchpoint(`${BusinessTimeEstablishedType.LessThanOneYear}`, 'Less Than A Year'),
    new Touchpoint(`${BusinessTimeEstablishedType.OneYear}`, '1 Year'),
    new Touchpoint(`${BusinessTimeEstablishedType.TwoYears}`, '2 Years'),
    new Touchpoint(`${BusinessTimeEstablishedType.ThreeYears}`, '3 Years'),
    new Touchpoint(`${BusinessTimeEstablishedType.FourYears}`, '4 Years'),
    new Touchpoint(`${BusinessTimeEstablishedType.FiveOrMoreYears}`, '5 Or More Years')
  ];

  public businessSizeOptions: Touchpoint[] = [
    new Touchpoint(`${BusinessSizeType.OneTo10}`, '1 - 10'),
    new Touchpoint(`${BusinessSizeType.TenTo50}`, '10 - 50'),
    new Touchpoint(`${BusinessSizeType.FiftyTo200}`, '50 - 200'),
    new Touchpoint(`${BusinessSizeType.MoreThan200}`, '200+')
  ];

  public businessTypeOptions: Touchpoint[] = [
    new Touchpoint(`${BusinessType.PLC}`, 'PLC'),
    new Touchpoint(`${BusinessType.LTDCompany}`, 'LTD Company'),
    new Touchpoint(`${BusinessType.LLPPartnership}`, 'LLP / Partnership'),
    new Touchpoint(`${BusinessType.SoleTraderSelfEmployed}`, 'Sole Trader / Self-employed')
  ];

  public get business(): IBusiness {
    return this.$store.getters['businessModule/business'];
  }

  public get businessSize(): BusinessSizeType {
    return this.$store.getters['businessModule/businessSize'];
  }

  public get businessType(): BusinessType {
    return this.$store.getters['businessModule/businessType'];
  }

  public get businessTimeEstablished(): number {
    return this.$store.getters['businessModule/businessTimeEstablished'];
  }

  public set businessSize(value: BusinessSizeType) {
    this.$store.dispatch('businessModule/submitBusinessSize', value);
  }

  public set businessType(value: BusinessType) {
    this.$store.dispatch('businessModule/submitBusinessType', value);
  }

  public set businessTimeEstablished(value: number) {
    this.$store.dispatch('businessModule/submitTimeEstablished', value);
  }

  public get existingFleetSize(): number {
    return this.$store.getters['businessModule/existingFleetSize'];
  }

  public set existingFleetSize(value: number) {
    this.$store.dispatch('businessModule/submitExistingFleetSize', value);
  }

  public get requiredFleetSize(): number {
    return this.$store.getters['businessModule/requiredFleetSize'];
  }

  public set requiredFleetSize(value: number) {
    this.$store.dispatch('businessModule/submitRequiredFleetSize', value);
  }

  public get businessName(): string {
    return this.$store.getters['businessModule/businessName'];
  }

  public set businessName(value: string) {
    this.$store.dispatch('businessModule/submitBusinessName', value);
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged: boolean = false) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  public get showBusinessInformation(): boolean {
    return (this.$store.getters['enquiryModule/customerType'] === CustomerType.Business || this.showBusinessInfo);
  }

  @Watch('existingFleetSize')
  public onexistingFleetSizeChangeEvent(newVal: number, oldVal: number) {
    this.existingFleetSizeChanged = oldVal !== newVal;
  }

  @Watch('requiredFleetSize')
  public onrequiredFleetSizeChangeEvent(newVal: number, oldVal: number) {
    this.requiredFleetSizeChanged = oldVal !== newVal;
  }

  public created() {
    this.$validator.localize('en', BusinessValidationMessages.english);
    this.hideSliders = mobileCheckService.hideSliders;
  }
}
</script>
